import React, { useState } from "react"
import { List, Checkbox, Divider, Input, Select, Tag, message } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { useUserStore } from "stores/userStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { RequestType } from "api/Superadmin/superadmin"
import {
  getServerFromHostname,
  serverOrigin,
  ServerUnit,
} from "utils/server"
import { setServerCookie, stashUnit } from "utils/serverCookies"

interface SuperadminDisplayProps {
  units: ServerUnit[]
  isLoading: boolean
  makeAdminApi: (
    route: string,
    requestType: RequestType,
    body?: string
  ) => Promise<{ data: unknown }>
  isCallingAdminApi: boolean
}

/**
 * SuperadminDisplay component provides an interface for selecting a unit and making API calls.
 * It allows users to specify the request type, route, and request body, and displays the API response.
 */
const SuperadminDisplay: React.FC<SuperadminDisplayProps> = (props) => {
  const { units, isLoading, makeAdminApi, isCallingAdminApi } = props
  const user = useUserStore((state) => state.user) // Get user state
  const updateUser = useUserStore((state) => state.updateUser) // Get update function
  const [route, setRoute] = useState<string>("")
  const [body, setBody] = useState<string>("")
  const [output, setOutput] = useState<string>("")
  const [requestType, setRequestType] = useState<RequestType>("get")
  const hostServer = getServerFromHostname()

  /**
   * Handles the selection of a unit. Units on the current server just update
   * the user store (as before). Units on another server require a hop to that
   * server's subdomain: the chosen unit is stashed in a short-lived cookie,
   * the dq_server cookie is updated, and the browser navigates there; after
   * the silent re-auth, useUser applies the stashed unit.
   */
  const handleUnitSelection = (unit: ServerUnit): void => {
    if (!hostServer || !unit.serverId || unit.serverId === hostServer) {
      updateUser({ unit: unit.name })
      notify.toast.success(`Unit set to ${unit.name}`)
      return
    }

    stashUnit(unit.name)
    setServerCookie(unit.serverId)
    // auto-dismissing loading toast — notify.toast.loading is sticky; keep antd message
    message.loading(`Switching to ${unit.serverId.toUpperCase()} server...`)
    window.location.assign(serverOrigin(unit.serverId) + "/superadmin")
  }
  /**
   * Handles making the API call with the specified route, request type, and body.
   * Updates the output state with the response or error message.
   */
  const handleApiCall = async (
    route: string,
    requestType: RequestType,
    body: string
  ): Promise<void> => {
    try {
      const res = await makeAdminApi(route, requestType, body)
      // Check if res is a string or an object and handle accordingly
      if (typeof res.data === "string") {
        setOutput(res.data)
      } else if (typeof res.data === "object") {
        setOutput(JSON.stringify(res.data, null, 2)) // Format the object as a pretty-printed JSON string
      } else {
        setOutput("Unexpected response type.")
      }
    } catch (error: unknown) {
      console.error("Error in API call:", error)
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      setOutput(`Error: ${errorMessage}`)
    }
  }
  /**
   * Handles changing the request type (GET, POST, DELETE) in the select dropdown.
   */
  const handleRequestTypeChange = (value: RequestType): void => {
    setRequestType(value)
  }

  return (
    <div className="flex flex-col p-5">
      <div className="bg-white rounded-md">
        <div className="flex flex-col p-2">
          <h1 className="text-2xl font-semibold m-0">Unit Selector</h1>
          <p className="text-lg font font-semibold mt-5">
            Current Unit: {user.unit}
          </p>
        </div>
        <List
          loading={isLoading}
          className="max-h-96 overflow-auto"
          itemLayout="horizontal"
          dataSource={[...units].sort((a, b) =>
            a.serverId === b.serverId ? 0 : a.serverId === hostServer ? -1 : 1
          )}
          renderItem={(item) => (
            <List.Item
              onClick={() => handleUnitSelection(item)}
              className="cursor-pointer hover:bg-gray-100 mx-1"
              actions={[
                <Checkbox
                  key={item.name}
                  onClick={() => handleUnitSelection(item)}
                  className="mr-2"
                  checked={user.unit === item.name}
                />,
              ]}
            >
              <List.Item.Meta
                className="p-2 ml-2"
                title={
                  <span>
                    {item.name}
                    {item.serverId && (
                      <Tag
                        className="ml-2"
                        color={item.serverId === hostServer ? "blue" : "orange"}
                      >
                        {item.serverId.toUpperCase()}
                      </Tag>
                    )}
                  </span>
                }
              />
            </List.Item>
          )}
        />
        <Divider />
        <div className="flex flex-col p-2">
          <h1 className="text-2xl font-semibold mt-0">API Queries</h1>
        </div>
        <div className="flex flex-row h-full px-2 space-x-2">
          <div className="flex flex-col w-full">
            <Input
              onChange={(e) => setRoute(e.target.value)}
              placeholder="Enter API Query here eg. /available-users"
            />
            <div className="flex flex-row mt-2 space-x-2">
              <Select
                style={{ width: 120 }}
                defaultValue={"get"}
                onChange={handleRequestTypeChange}
                options={[
                  { value: "get", label: "GET" },
                  { value: "post", label: "POST" },
                  { value: "delete", label: "DELETE" },
                ]}
              />
              <Input.TextArea
                placeholder="Enter request body here in JSON (if any) eg { 'name': 'hello', 'goal': 'hello' }"
                onChange={(e) => setBody(e.target.value)}
                autoSize={{ minRows: 5, maxRows: 10 }}
              />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <Input.TextArea
              placeholder="Output will be displayed here"
              value={output}
              autoSize={{ minRows: 5, maxRows: 10 }}
            />
          </div>
        </div>{" "}
        <DataQButton
          onClick={() => handleApiCall(route, requestType, body)}
          className="m-2"
          type="primary"
          isLoading={isCallingAdminApi}
        >
          Run API
        </DataQButton>
      </div>
    </div>
  )
}

export default SuperadminDisplay
